import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, formatNumber } from '../lib/utils';
import { 
  Wheat, 
  Plus, 
  Users, 
  Package,
  TrendingUp,
  TrendingDown,
  Warehouse,
  ShoppingCart,
  Phone,
  MapPin,
  Scale,
  CalendarIcon,
  Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ExportButton from '../components/ExportButton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Tab content components (extracted Iter24 to slim this page from 904 → ~510 lines)
import MaizeOverviewTab from '../components/maize/MaizeOverviewTab';
import MaizeFarmersTab from '../components/maize/MaizeFarmersTab';
import MaizePurchasesTab from '../components/maize/MaizePurchasesTab';
import MaizeSalesTab from '../components/maize/MaizeSalesTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#F9A825', '#2E7D32', '#F57C00', '#1E88E5', '#8D6E63'];

const MaizeProgramPage = () => {
  const { hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('purchases');
  const [summary, setSummary] = useState(null);
  const [farmers, setFarmers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [farmerDialogOpen, setFarmerDialogOpen] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  
  // Form states
  const [farmerForm, setFarmerForm] = useState({
    name: '',
    phone: '',
    village: '',
    farm_size_acres: '',
  });
  
  const [purchaseForm, setPurchaseForm] = useState({
    farmer_id: '',
    quantity_kg: '',
    price_per_kg: '',
    date: new Date(),
    quality_grade: 'A',
    notes: '',
  });
  
  const [saleForm, setSaleForm] = useState({
    quantity_kg: '',
    price_per_kg: '',
    date: new Date(),
    buyer_name: '',
    buyer_contact: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, farmersRes, purchasesRes, salesRes] = await Promise.all([
        axios.get(`${API_URL}/api/maize/summary`),
        axios.get(`${API_URL}/api/maize/farmers`),
        axios.get(`${API_URL}/api/maize/purchases`),
        axios.get(`${API_URL}/api/maize/sales`),
      ]);
      setSummary(summaryRes.data);
      setFarmers(farmersRes.data);
      setPurchases(purchasesRes.data);
      setSales(salesRes.data);
    } catch (error) {
      toast.error('Failed to load maize program data');
    } finally {
      setLoading(false);
    }
  };

  const downloadReceipt = async (purchaseId) => {
    try {
      const res = await axios.get(`${API_URL}/api/exports/maize-receipt/${purchaseId}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `suubi_receipt_${purchaseId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch (err) {
      toast.error('Failed to download receipt');
    }
  };

  // Edit mode + delete confirmation state
  const [editingFarmerId, setEditingFarmerId] = useState(null);
  const [editingPurchaseId, setEditingPurchaseId] = useState(null);
  const [editingSaleId, setEditingSaleId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);  // { kind, item }

  const resetFarmerForm = () => { setFarmerForm({ name: '', phone: '', village: '', farm_size_acres: '' }); setEditingFarmerId(null); };
  const resetPurchaseForm = () => { setPurchaseForm({ farmer_id: '', quantity_kg: '', price_per_kg: '', date: new Date(), quality_grade: 'A', notes: '' }); setEditingPurchaseId(null); };
  const resetSaleForm = () => { setSaleForm({ quantity_kg: '', price_per_kg: '', date: new Date(), buyer_name: '', buyer_contact: '', notes: '' }); setEditingSaleId(null); };

  const openEditFarmer = (f) => {
    setEditingFarmerId(f.id);
    setFarmerForm({ name: f.name || '', phone: f.phone || '', village: f.village || '', farm_size_acres: f.farm_size_acres ?? '' });
    setFarmerDialogOpen(true);
  };
  const openEditPurchase = (p) => {
    setEditingPurchaseId(p.id);
    setPurchaseForm({
      farmer_id: p.farmer_id || '',
      quantity_kg: String(p.quantity_kg ?? ''),
      price_per_kg: String(p.price_per_kg ?? ''),
      date: p.date ? new Date(p.date) : new Date(),
      quality_grade: p.quality_grade || 'A',
      notes: p.notes || '',
    });
    setPurchaseDialogOpen(true);
  };
  const openEditSale = (s) => {
    setEditingSaleId(s.id);
    setSaleForm({
      quantity_kg: String(s.quantity_kg ?? ''),
      price_per_kg: String(s.price_per_kg ?? ''),
      date: s.date ? new Date(s.date) : new Date(),
      buyer_name: s.buyer_name || '',
      buyer_contact: s.buyer_contact || '',
      notes: s.notes || '',
    });
    setSaleDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { kind, item } = deleteTarget;
    try {
      await axios.delete(`${API_URL}/api/maize/${kind}/${item.id}`);
      toast.success(`${kind === 'farmers' ? 'Farmer' : kind === 'purchases' ? 'Purchase' : 'Sale'} deleted`);
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleFarmerSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...farmerForm,
        farm_size_acres: farmerForm.farm_size_acres ? parseFloat(farmerForm.farm_size_acres) : null,
      };
      if (editingFarmerId) {
        await axios.put(`${API_URL}/api/maize/farmers/${editingFarmerId}`, payload);
        toast.success('Farmer updated');
      } else {
        await axios.post(`${API_URL}/api/maize/farmers`, payload);
        toast.success('Farmer registered successfully');
      }
      setFarmerDialogOpen(false);
      resetFarmerForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingFarmerId ? 'update' : 'register'} farmer`);
    }
  };

  const handlePurchaseSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...purchaseForm,
        quantity_kg: parseFloat(purchaseForm.quantity_kg),
        price_per_kg: parseFloat(purchaseForm.price_per_kg),
        date: format(purchaseForm.date, 'yyyy-MM-dd'),
      };
      if (editingPurchaseId) {
        await axios.put(`${API_URL}/api/maize/purchases/${editingPurchaseId}`, payload);
        toast.success('Purchase updated');
      } else {
        await axios.post(`${API_URL}/api/maize/purchases`, payload);
        toast.success('Purchase recorded successfully');
      }
      setPurchaseDialogOpen(false);
      resetPurchaseForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingPurchaseId ? 'update' : 'record'} purchase`);
    }
  };

  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...saleForm,
        quantity_kg: parseFloat(saleForm.quantity_kg),
        price_per_kg: parseFloat(saleForm.price_per_kg),
        date: format(saleForm.date, 'yyyy-MM-dd'),
      };
      if (editingSaleId) {
        await axios.put(`${API_URL}/api/maize/sales/${editingSaleId}`, payload);
        toast.success('Sale updated');
      } else {
        await axios.post(`${API_URL}/api/maize/sales`, payload);
        toast.success('Sale recorded successfully');
      }
      setSaleDialogOpen(false);
      resetSaleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingSaleId ? 'update' : 'record'} sale`);
    }
  };

  const canEdit = hasRole(['admin', 'project_manager', 'finance_officer']);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Hero */}
      <div 
        className="rounded-xl bg-cover bg-center relative overflow-hidden h-48"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1200')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-amber-600/90 to-amber-500/70" />
        <div className="absolute inset-0 flex items-center p-8">
          <div className="text-white">
            <div className="flex items-center gap-3 mb-2">
              <Wheat className="h-8 w-8" />
              <h2 className="font-heading text-3xl font-bold">Maize Buy-back Program</h2>
            </div>
            <p className="text-white/90 max-w-2xl">
              Empowering women farmers through fair-price maize purchasing. We buy from local women farmers, 
              store strategically, and sell at optimal times to generate sustainable revenue.
            </p>
          </div>
        </div>
        <div className="absolute top-4 right-4">
          <ExportButton
            path="exports/maize-purchases"
            filenameBase="suubi_maize_purchases"
            label="Export Purchases"
            testId="maize-export-btn"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="maize-summary">
        <Card className="card-hover bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100 dark:bg-amber-900/20">
                <Users className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Registered Farmers</p>
                <p className="text-2xl font-heading font-bold">{summary?.total_farmers || 0}</p>
                <p className="text-xs text-amber-600">{summary?.active_farmers || 0} active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/20">
                <ShoppingCart className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Purchased</p>
                <p className="text-2xl font-heading font-bold">{formatNumber(summary?.total_purchased_kg || 0)} kg</p>
                <p className="text-xs text-green-600">{formatCurrency(summary?.total_purchased_amount || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-900/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/20">
                <Warehouse className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Stock</p>
                <p className="text-2xl font-heading font-bold">{formatNumber(summary?.current_stock_kg || 0)} kg</p>
                <p className="text-xs text-blue-600">In storage</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover bg-purple-50 dark:bg-purple-900/10 border-purple-200 dark:border-purple-900/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/20">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gross Profit</p>
                <p className={cn("text-2xl font-heading font-bold", (summary?.gross_profit || 0) >= 0 ? "text-green-600" : "text-red-600")}>
                  {formatCurrency(summary?.gross_profit || 0)}
                </p>
                <p className="text-xs text-purple-600">{summary?.profit_margin || 0}% margin</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Price Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Purchase Price</p>
                <p className="text-2xl font-heading font-bold text-green-600">
                  {formatCurrency(summary?.avg_purchase_price || 0)}/kg
                </p>
                <p className="text-xs text-muted-foreground mt-1">Fair price paid to farmers</p>
              </div>
              <TrendingDown className="h-10 w-10 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Sale Price</p>
                <p className="text-2xl font-heading font-bold text-blue-600">
                  {formatCurrency(summary?.avg_sale_price || 0)}/kg
                </p>
                <p className="text-xs text-muted-foreground mt-1">Market sale price</p>
              </div>
              <TrendingUp className="h-10 w-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="purchases">Active Purchases</TabsTrigger>
            <TabsTrigger value="farmers">Farmers</TabsTrigger>
            <TabsTrigger value="sales">Sales</TabsTrigger>
            <TabsTrigger value="overview">Analytics</TabsTrigger>
          </TabsList>
          
          {canEdit && (
            <div className="flex gap-2">
              <Dialog open={farmerDialogOpen} onOpenChange={(o) => { setFarmerDialogOpen(o); if (!o) resetFarmerForm(); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="add-farmer-btn">
                    <Plus className="mr-1 h-4 w-4" />
                    Add Farmer
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading">{editingFarmerId ? 'Edit Farmer' : 'Register Farmer'}</DialogTitle>
                    <DialogDescription>Add a new woman farmer to the program</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleFarmerSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        value={farmerForm.name}
                        onChange={(e) => setFarmerForm({ ...farmerForm, name: e.target.value })}
                        placeholder="Farmer's full name"
                        required
                        data-testid="farmer-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={farmerForm.phone}
                          onChange={(e) => setFarmerForm({ ...farmerForm, phone: e.target.value })}
                          placeholder="+256..."
                          required
                          data-testid="farmer-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Village</Label>
                        <Input
                          value={farmerForm.village}
                          onChange={(e) => setFarmerForm({ ...farmerForm, village: e.target.value })}
                          placeholder="Village name"
                          required
                          data-testid="farmer-village"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Farm Size (acres) - Optional</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={farmerForm.farm_size_acres}
                        onChange={(e) => setFarmerForm({ ...farmerForm, farm_size_acres: e.target.value })}
                        placeholder="e.g., 2.5"
                        data-testid="farmer-farm-size"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setFarmerDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" data-testid="farmer-submit-btn">{editingFarmerId ? 'Update Farmer' : 'Register Farmer'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={purchaseDialogOpen} onOpenChange={(o) => { setPurchaseDialogOpen(o); if (!o) resetPurchaseForm(); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200" data-testid="add-purchase-btn">
                    <ShoppingCart className="mr-1 h-4 w-4" />
                    Buy Maize
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading">{editingPurchaseId ? 'Edit Purchase' : 'Record Purchase'}</DialogTitle>
                    <DialogDescription>Record maize bought from a farmer</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handlePurchaseSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Select Farmer</Label>
                      <Select
                        value={purchaseForm.farmer_id}
                        onValueChange={(value) => setPurchaseForm({ ...purchaseForm, farmer_id: value })}
                        required
                      >
                        <SelectTrigger data-testid="purchase-farmer-select">
                          <SelectValue placeholder="Choose farmer" />
                        </SelectTrigger>
                        <SelectContent>
                          {farmers.map((f) => (
                            <SelectItem key={f.id} value={f.id}>{f.name} - {f.village}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Quantity (kg)</Label>
                        <Input
                          type="number"
                          value={purchaseForm.quantity_kg}
                          onChange={(e) => setPurchaseForm({ ...purchaseForm, quantity_kg: e.target.value })}
                          placeholder="e.g., 500"
                          required
                          data-testid="purchase-quantity"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Price per kg (UGX)</Label>
                        <Input
                          type="number"
                          value={purchaseForm.price_per_kg}
                          onChange={(e) => setPurchaseForm({ ...purchaseForm, price_per_kg: e.target.value })}
                          placeholder="e.g., 1200"
                          required
                          data-testid="purchase-price"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(purchaseForm.date, 'PP')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={purchaseForm.date}
                              onSelect={(date) => setPurchaseForm({ ...purchaseForm, date })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Quality Grade</Label>
                        <Select
                          value={purchaseForm.quality_grade}
                          onValueChange={(value) => setPurchaseForm({ ...purchaseForm, quality_grade: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A">Grade A (Premium)</SelectItem>
                            <SelectItem value="B">Grade B (Standard)</SelectItem>
                            <SelectItem value="C">Grade C (Basic)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {purchaseForm.quantity_kg && purchaseForm.price_per_kg && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Amount</p>
                        <p className="text-xl font-bold text-green-600">
                          {formatCurrency(parseFloat(purchaseForm.quantity_kg || 0) * parseFloat(purchaseForm.price_per_kg || 0))}
                        </p>
                      </div>
                    )}
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setPurchaseDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="purchase-submit-btn">{editingPurchaseId ? 'Update Purchase' : 'Record Purchase'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={saleDialogOpen} onOpenChange={(o) => { setSaleDialogOpen(o); if (!o) resetSaleForm(); }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-sale-btn">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    Sell Maize
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading">{editingSaleId ? 'Edit Sale' : 'Record Sale'}</DialogTitle>
                    <DialogDescription>Record maize sold to market</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Buyer Name</Label>
                      <Input
                        value={saleForm.buyer_name}
                        onChange={(e) => setSaleForm({ ...saleForm, buyer_name: e.target.value })}
                        placeholder="Buyer/Company name"
                        required
                        data-testid="sale-buyer"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Quantity (kg)</Label>
                        <Input
                          type="number"
                          value={saleForm.quantity_kg}
                          onChange={(e) => setSaleForm({ ...saleForm, quantity_kg: e.target.value })}
                          placeholder="e.g., 1000"
                          required
                          data-testid="sale-quantity"
                        />
                        <p className="text-xs text-muted-foreground">Available: {formatNumber(summary?.current_stock_kg || 0)} kg</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Price per kg (UGX)</Label>
                        <Input
                          type="number"
                          value={saleForm.price_per_kg}
                          onChange={(e) => setSaleForm({ ...saleForm, price_per_kg: e.target.value })}
                          placeholder="e.g., 1500"
                          required
                          data-testid="sale-price"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(saleForm.date, 'PP')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={saleForm.date}
                              onSelect={(date) => setSaleForm({ ...saleForm, date })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Buyer Contact</Label>
                        <Input
                          value={saleForm.buyer_contact}
                          onChange={(e) => setSaleForm({ ...saleForm, buyer_contact: e.target.value })}
                          placeholder="+256..."
                          data-testid="sale-contact"
                        />
                      </div>
                    </div>
                    {saleForm.quantity_kg && saleForm.price_per_kg && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Amount</p>
                        <p className="text-xl font-bold text-blue-600">
                          {formatCurrency(parseFloat(saleForm.quantity_kg || 0) * parseFloat(saleForm.price_per_kg || 0))}
                        </p>
                      </div>
                    )}
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setSaleDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="sale-submit-btn">{editingSaleId ? 'Update Sale' : 'Record Sale'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <MaizeOverviewTab summary={summary} />
        </TabsContent>

        {/* Farmers Tab */}
        <TabsContent value="farmers" className="mt-6">
          <MaizeFarmersTab
            farmers={farmers}
            canEdit={canEdit}
            onEdit={openEditFarmer}
            onDelete={(f) => setDeleteTarget({ kind: 'farmers', item: f })}
          />
        </TabsContent>

        {/* Purchases Tab */}
        <TabsContent value="purchases" className="mt-6">
          <MaizePurchasesTab
            purchases={purchases}
            onDownloadReceipt={downloadReceipt}
            canEdit={canEdit}
            onEdit={openEditPurchase}
            onDelete={(p) => setDeleteTarget({ kind: 'purchases', item: p })}
          />
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="sales" className="mt-6">
          <MaizeSalesTab
            sales={sales}
            canEdit={canEdit}
            onEdit={openEditSale}
            onDelete={(s) => setDeleteTarget({ kind: 'sales', item: s })}
          />
        </TabsContent>

      </Tabs>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-maize-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.kind === 'farmers' ? 'farmer' : deleteTarget?.kind === 'purchases' ? 'purchase' : 'sale'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.kind === 'farmers' && deleteTarget?.item ? (
                <>Delete farmer <strong>{deleteTarget.item.name}</strong> ({deleteTarget.item.village || 'no village'})? You can only delete farmers with no purchase history.</>
              ) : deleteTarget?.kind === 'purchases' && deleteTarget?.item ? (
                <>Delete purchase of <strong>{deleteTarget.item.quantity_kg}kg</strong> from <strong>{deleteTarget.item.farmer_name}</strong> ({formatCurrency(deleteTarget.item.total_amount)})? This action cannot be undone.</>
              ) : deleteTarget?.kind === 'sales' && deleteTarget?.item ? (
                <>Delete sale to <strong>{deleteTarget.item.buyer_name}</strong> ({deleteTarget.item.quantity_kg}kg, {formatCurrency(deleteTarget.item.total_amount)})? This action cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-maize-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MaizeProgramPage;
